import { useRef, useState } from "react";
import { Upload, FileCheck2, X, Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";

/**
 * FileUpload — single-file upload to /api/uploads.
 * Shows inline thumbnail for images (or PDF icon) + scan-status badge.
 */
export default function FileUpload({
  label,
  hint,
  kind,
  value,
  onChange,
  accept = "image/jpeg,image/png,image/webp,application/pdf",
  testId = "upload",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // Keep a same-session preview URL for instant feedback while admin/later views go through AuthImage.
  const [localPreview, setLocalPreview] = useState(null);

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10 MB)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/uploads?kind=${encodeURIComponent(kind)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Local preview using FileReader (no auth round-trip)
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => setLocalPreview(e.target?.result || null);
        reader.readAsDataURL(file);
      } else {
        setLocalPreview(null);
      }
      onChange({
        id: data.id,
        original_filename: data.original_filename,
        content_type: data.content_type,
        size: data.size,
        kind,
        is_image: data.is_image,
        scan_status: data.scan_status,
        scan_detail: data.scan_detail,
      });
      if (data.scan_status === "suspicious") {
        toast.warning(`Uploaded with warning: ${data.scan_detail}`);
      } else {
        toast.success("Uploaded · scan passed");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleRemove = () => {
    setLocalPreview(null);
    onChange(null);
  };

  const sizeLabel = value?.size
    ? value.size < 1024 * 1024
      ? `${Math.round(value.size / 1024)} KB`
      : `${(value.size / (1024 * 1024)).toFixed(1)} MB`
    : "";

  return (
    <div className="rounded-xl border border-dashed border-[#5B2C82]/30 bg-[#F9F6F0] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading text-sm font-semibold text-ink">{label}</div>
          {hint && <div className="mt-0.5 text-xs text-ink-muted">{hint}</div>}
        </div>
        {value ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-900">
            <FileCheck2 className="h-3.5 w-3.5" /> Uploaded
          </span>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {value ? (
        <div className="mt-3 flex items-center gap-3 rounded-lg bg-white p-2.5 text-sm">
          {/* Thumbnail */}
          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md border border-[#E5E2DE] bg-[#F9F6F0]" data-testid={`${testId}-thumb`}>
            {localPreview ? (
              <img src={localPreview} alt="" className="h-full w-full object-cover" />
            ) : (
              <AuthImage fileId={value.id} contentType={value.content_type} className="h-full w-full" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-ink">{value.original_filename}</div>
            <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-muted">
              <span>{sizeLabel}</span>
              {value.scan_status === "clean" && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-emerald-700" data-testid={`${testId}-scan-clean`}>
                  <ShieldCheck className="h-3 w-3" /> Scanned · clean
                </span>
              )}
              {value.scan_status === "suspicious" && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-amber-800" data-testid={`${testId}-scan-suspicious`}>
                  <ShieldAlert className="h-3 w-3" /> Suspicious
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="rounded-full p-1 text-ink-muted hover:bg-rose-50 hover:text-rose-700"
            data-testid={`${testId}-remove`}
            aria-label="Remove file"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={handlePick}
          disabled={busy}
          className="mt-3 w-full border-[#5B2C82]/40 bg-white text-[#5B2C82] hover:bg-[#5B2C82] hover:text-white"
          data-testid={`${testId}-btn`}
        >
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading & scanning…
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" /> Choose file (JPG, PNG, PDF · max 10 MB)
            </>
          )}
        </Button>
      )}
    </div>
  );
}
